import { defineStore } from "pinia";
import * as Sentry from "@sentry/vue";

/* eslint-disable no-console */

export const useCartStore = defineStore("cartStore", () => {
  const cartData = ref();
  const cartMerged = ref(false);
  const cartCode = ref<string>("");
  const cartInitialized = ref(false);
  const cartValidationCache = ref<{ timestamp: number; valid: boolean } | null>(
    null,
  );
  const { $api } = useNuxtApp();
  const debug = false;

  // Cache cart validation for 5 minutes to avoid repeated API calls
  const CART_VALIDATION_CACHE_DURATION = 5 * 60 * 1000;

  const createNewCart = async () => {
    if (debug)
      console.log(
        "createNewCart start. Let's clear localStorage: webshop.cart.code",
      );

    try {
      const newCartCode = (await $api("cart-new")) as string;
      if (debug) console.log("newCartData arrived", newCartCode);

      if (newCartCode) {
        cartCode.value = newCartCode;
        localStorage.setItem("webshop.cart.code", newCartCode);
        cartData.value = null;
        cartValidationCache.value = null; // Clear cache when creating new cart
        return true;
      } else {
        if (debug)
          console.error("Error while trying to create new cart in API");
        return false;
      }
    } catch (err: any) {
      if (debug)
        console.error(
          "Error while trying to create new cart in API:",
          err.message,
        );

      Sentry.captureEvent({
        message: `Error while trying to create new cart in API`,
        level: "error",
        extra: {
          file: "stores/cartStore.ts",
          key: "ER-KL3T",
          error: err,
        },
      });
      return false;
    }
  };

  const validateCartCode = async (code: string): Promise<boolean> => {
    // Check cache first
    if (
      cartValidationCache.value &&
      Date.now() - cartValidationCache.value.timestamp <
        CART_VALIDATION_CACHE_DURATION
    ) {
      return cartValidationCache.value.valid;
    }

    try {
      const cartExists = (await $api(`cart-exists/${code}`)) as Record<
        string,
        any
      >;
      if (debug) console.log("cartExists", cartExists);

      const isValid = cartExists.exists === true;
      cartMerged.value = cartExists.itemsMerged || false;

      // Cache the validation result
      cartValidationCache.value = {
        timestamp: Date.now(),
        valid: isValid,
      };

      return isValid;
    } catch (err: any) {
      if (debug)
        console.error("Error when checking if cart exists", err.message);

      Sentry.captureEvent({
        message: `Error when checking if cart exists`,
        level: "error",
        extra: {
          file: "stores/cartStore.ts",
          key: "ER-DKP6",
          error: err,
        },
      });
      return false;
    }
  };

  const initCart = async (forceRefresh = false) => {
    if (debug) console.log("initCart start");

    // If already initialized and not forcing refresh, return early
    if (cartInitialized.value && !forceRefresh && cartCode.value) {
      return true;
    }

    // Get cart code from localStorage or use cached one
    const storedCartCode =
      localStorage.getItem("webshop.cart.code") || cartCode.value;

    if (storedCartCode && !forceRefresh) {
      cartCode.value = storedCartCode;

      // Validate existing cart
      const isValid = await validateCartCode(storedCartCode);
      if (isValid) {
        cartInitialized.value = true;
        return true;
      }
    }

    // Create new cart if needed
    const success = await createNewCart();
    if (success) {
      cartInitialized.value = true;
      return true;
    }

    return false;
  };

  const getCartCode = async () => {
    if (cartCode.value) {
      return cartCode.value;
    }

    const cartReady = await initCart();
    if (cartReady) {
      return cartCode.value;
    }
    return null;
  };

  const addProduct = async (productId: number, quantity: number) => {
    if (debug) console.log("adding: ", productId, quantity, cartCode.value);

    // Ensure cart is initialized
    if (!cartInitialized.value) {
      const cartReady = await initCart();
      if (!cartReady) return false;
    }

    try {
      await $api("cart-item", {
        method: "POST",
        body: {
          code: cartCode.value,
          product: productId,
          quantity,
        },
      });

      // Fetch cart data immediately after adding product
      await fetchCartData(true);
      return true;
    } catch (err) {
      if (debug) console.error("Error adding product to cart:", err);
      return false;
    }
  };

  const fetchCartData = async (forceRefresh = false) => {
    // Don't fetch if cart is not initialized
    if (!cartInitialized.value) {
      const cartReady = await initCart();
      if (!cartReady) return;
    }

    if (debug) console.log("fetching cart data: ", cartCode.value);

    if (!cartCode.value) return;

    try {
      const cart = await $api(`cart/${cartCode.value}`);
      if (debug) console.log("cart", cart);
      cartData.value = cart;
    } catch (err: any) {
      if (debug) console.error("Error when getting cart data", err.message);

      // Clear invalid cart
      cartCode.value = "";
      cartData.value = null;
      cartInitialized.value = false;
      cartValidationCache.value = null;
      localStorage.setItem("webshop.cart.code", "");
    }
  };

  const clearCartMerged = () => {
    cartMerged.value = false;
  };

  // Initialize cart code from localStorage on store creation
  if (process.client) {
    const storedCode = localStorage.getItem("webshop.cart.code");
    if (storedCode) {
      cartCode.value = storedCode;
    }
  }

  return {
    // export these during development if you need to debug them in vue devtools
    // -----------

    // real exports
    addProduct,
    cartData,
    cartMerged,
    clearCartMerged,
    fetchCartData,
    getCartCode,
    cartCode,
    cartInitialized,
  } as const;
});
